import { useNavigate } from '@tanstack/react-router'
import { CtaButton } from '@workspace/ui/components/dsm/cta-button'
import { SecondaryButton } from '@workspace/ui/components/dsm/secondary-button'
import { cn } from '@workspace/ui/lib/utils'
import { Ban, Ellipsis, FileText, Pencil, Plus, Share2, X } from 'lucide-react'
import { useEffect, useMemo, useRef } from 'react'
import { useInvestorContext } from '../../../../contexts/investor-guard'
import { trackEvent } from '../../../../lib/gtag'
import { copy } from '../../copy/investor-copy'
import {
  isResourceNotFound,
  useMarkWatchdogSeenOnOpen,
  useStartupRelationship,
} from '../../hooks/use-investor-assistant'
import { ActionMenu, type MenuAction } from '../action-menu'
import { roundLabel } from '../following/round'
import { StartupTile } from '../following/startup-tile'
import { highlightedQuote } from '../search/highlight'
import { primaryAction } from './primary-action'
import { RelationshipCards, useRelationshipActions } from './relationship-cards'
import type { CardActions, Relationship } from './types'

/** The line under a startup's name: its round, sector and city, when it has them. */
export function metaLine(startup: {
  round: Relationship['startup']['round'] | null
  sector?: string | null
  city: string | null
}): string {
  return [startup.round && roundLabel(startup.round), startup.sector, startup.city]
    .filter(Boolean)
    .join(' · ')
}

/**
 * The startup beside a list: who it is, what the investor's follow of it is, what the founder
 * published since, the saved read and the pass. Not modal: the list stays usable beside it.
 */
export type MatchedPassage = {
  text: string
  source: string
  date: string | null
  /** The words the search looked for, marked in the quote as the result card marks them. */
  words: string[]
}

export function StartupSidePanel({
  startupId,
  from,
  matched,
  writeNote,
  startFollow,
  onPassChange,
  onFollowChange,
  onClose,
}: {
  startupId: string
  /** Open with the note in edit mode (a row's "Write a note"). */
  writeNote?: boolean
  /** Open with the follow window (a pass window's "Follow instead"). */
  startFollow?: boolean
  /** Tells the search row that a pass was made or undone here. */
  onPassChange?: (startupId: string, passed: boolean) => void
  /** Tells the search row that a follow started or stopped here. */
  onFollowChange?: (startupId: string, following: boolean) => void
  /** Where the panel was opened, so the page it leads to can say where Back goes. */
  from?: 'search'
  /** The founder passage that matched the search this panel was opened from. */
  matched?: MatchedPassage | null
  onClose: () => void
}) {
  const query = useStartupRelationship(startupId)
  const data = query.data
  const navigate = useNavigate()
  const { investor } = useInvestorContext()
  const headingRef = useRef<HTMLHeadingElement>(null)
  useMarkWatchdogSeenOnOpen(startupId)
  // Escape closes the panel, unless a dialog, menu or popover above it is open: that one closes first.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      if (document.querySelector('[role="dialog"][data-state="open"], [role="menu"]')) return
      onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])
  useEffect(() => {
    trackEvent('startup_viewed', {
      startup_id: startupId,
      surface: 'panel',
      source: from ?? 'following',
    })
  }, [startupId, from])

  const token = data?.startup.profileLinkToken ?? null
  const openProfile = useMemo(
    () =>
      token
        ? () =>
            navigate({
              to: '/app/investor/$id/startup/$token',
              params: { id: investor.id, token },
              search: from ? { from } : {},
            })
        : null,
    [navigate, token, investor.id, from]
  )
  const { actions, dialogs, note } = useRelationshipActions({
    startupId,
    data,
    openProfile,
    onBack: onClose,
    startNote: writeNote,
    startFollow,
    onPassChange,
    onFollowChange,
  })

  return (
    <aside
      aria-label={copy('panel.aria.details', { startup: data?.startup.name ?? '' })}
      className="sf-panel w-[400px] shrink-0 overflow-y-auto border-l border-border bg-background"
    >
      {query.isLoading && (
        <output aria-label={copy('panel.loading', { startup: '' })} className="block space-y-4 p-4">
          <div className="h-16 animate-pulse rounded-xl bg-card/60" />
          <div className="h-32 animate-pulse rounded-xl bg-card/60" />
        </output>
      )}
      {query.isError && (
        <div className="space-y-3 p-5">
          <p className="text-sm text-muted-foreground">
            {isResourceNotFound(query.error) ? copy('panel.refused') : copy('panel.error')}
          </p>
          <div className="flex gap-2">
            <SecondaryButton size="sm" onClick={() => query.refetch()}>
              {copy('panel.retry')}
            </SecondaryButton>
            <SecondaryButton size="sm" onClick={onClose}>
              {copy('panel.aria.close')}
            </SecondaryButton>
          </div>
        </div>
      )}
      {data && (
        <>
          <PanelHeader
            data={data}
            headingRef={headingRef}
            onClose={onClose}
            openProfile={openProfile}
            actions={actions}
            fromSearch={from === 'search'}
          />
          <div className="space-y-4 p-4 pb-24">
            {from === 'search' && matched && <MatchedCard passage={matched} />}
            <RelationshipCards data={data} variant="panel" actions={actions} note={note} />
          </div>
          {dialogs}
        </>
      )}
    </aside>
  )
}

function PanelHeader({
  data,
  headingRef,
  onClose,
  openProfile,
  actions,
  fromSearch,
}: {
  data: Relationship
  headingRef: React.RefObject<HTMLHeadingElement | null>
  onClose: () => void
  openProfile: (() => void) | null
  actions: CardActions
  fromSearch: boolean
}) {
  const { startup, state } = data
  const menuButton = useRef<HTMLButtonElement>(null)
  const followButton = useRef<HTMLButtonElement>(null)
  // Not followed, with a profile to open: the follow sits beside Open profile. From the panel a follow goes by
  // the startup's link: without one (stealth) it is only made from its page.
  const canFollow = state === 'NONE' && startup.profileLinkToken !== null
  const action = primaryAction({
    state,
    variant: 'panel',
    hasProfileLink: startup.profileLinkToken !== null,
  })
  const waiting = state === 'WAITING_CHECK' || state === 'WAITING_PLACE'
  const following = state === 'ACTIVE' || state === 'CONDITION_MET'
  // Open profile is orange unless another action leads (see plans, follow) or none can.
  const ProfileButton = action === 'open-profile' ? CtaButton : SecondaryButton
  const menu: MenuAction[] = [
    ...(following || waiting
      ? [
          {
            key: 'edit',
            icon: Pencil,
            title: copy('menu.editFollow'),
            onSelect: () => actions.onEditFollow(menuButton.current),
          },
        ]
      : []),
    {
      key: 'note',
      icon: FileText,
      title: copy('menu.writeNote'),
      onSelect: actions.onWriteNote,
    },
    // Not followed and not passed yet: a pass is private and never a follow.
    ...(state === 'NONE' && data.decision === null
      ? [{ key: 'pass', icon: Ban, title: copy('search.result.pass'), onSelect: actions.onPass }]
      : []),
    ...(data.startup.shareable
      ? [
          {
            key: 'share',
            icon: Share2,
            title: copy('search.result.share'),
            onSelect: actions.onShare,
          },
        ]
      : []),
    ...(following || waiting
      ? [
          {
            key: 'stop',
            icon: X,
            title: copy('menu.stopFollowing'),
            destructive: true,
            onSelect: actions.onCancelFollow,
          },
        ]
      : []),
  ]

  return (
    <header className="border-b border-border px-5 pb-4 pt-4">
      {fromSearch && (
        <p className="mb-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {copy('panel.fromSearch')}
        </p>
      )}
      <div className="flex items-start gap-3">
        <StartupTile name={startup.name} size="lg" />
        <div className="min-w-0 flex-1">
          <h2 ref={headingRef} className="truncate text-base font-semibold text-foreground">
            {startup.name}
          </h2>
          {startup.description && (
            <p
              title={startup.description}
              className="mt-0.5 line-clamp-2 text-sm text-muted-foreground"
            >
              {startup.description}
            </p>
          )}
          <p className="mt-1 text-xs tabular-nums text-muted-foreground">{metaLine(startup)}</p>
        </div>
        <button
          type="button"
          aria-label={copy('panel.aria.close')}
          onClick={onClose}
          className="inline-flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-white/[0.06] hover:text-foreground"
        >
          <X className="size-4" />
        </button>
      </div>
      <div className="mt-3 flex items-center gap-2">
        <ProfileButton
          size="sm"
          disabled={!openProfile || state === 'ENDED_BY_FOUNDER'}
          onClick={() => openProfile?.()}
          className={cn(!openProfile && 'cursor-not-allowed')}
        >
          {copy('panel.openProfile')}
        </ProfileButton>
        {canFollow && (
          <SecondaryButton
            ref={followButton}
            size="sm"
            icon={<Plus />}
            onClick={() => actions.onFollow(undefined, followButton.current)}
          >
            {copy('page.follow')}
          </SecondaryButton>
        )}
        {menu.length > 0 && (
          <ActionMenu
            label={copy('panel.aria.more', { startup: startup.name })}
            actions={menu}
            trigger={
              <button
                ref={menuButton}
                type="button"
                aria-label={copy('panel.aria.more', { startup: startup.name })}
                className="inline-flex size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-white/[0.06] hover:text-foreground"
              >
                <Ellipsis className="size-4" />
              </button>
            }
          />
        )}
      </div>
      {!openProfile && <p className="mt-2 text-xs text-muted-foreground">{copy('panel.noLink')}</p>}
    </header>
  )
}

/** "What matched your search": the founder passage the search quoted, with its source and date. */
function MatchedCard({ passage }: { passage: MatchedPassage }) {
  return (
    <section className="space-y-2 rounded-xl border border-border p-4">
      <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {copy('panel.matched.heading')}
      </h3>
      <blockquote className="rounded-lg bg-[#0F0F10] px-3 py-2.5 text-sm leading-relaxed text-foreground/80">
        {'“'}
        {highlightedQuote(passage.text, passage.words)}
        {'”'}
      </blockquote>
      <span className="inline-flex items-center gap-1.5 rounded-[5px] border border-emerald-500/35 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wide text-emerald-500">
        {passage.source}
        {passage.date ? ` · ${passage.date}` : ''}
      </span>
    </section>
  )
}
